/**
 * DesktopShell — production-grade desktop layout.
 *
 * Three-region grid:
 *   · Left sidebar (272px fixed) — brand wordmark + nav + user footer
 *   · Top bar (sticky, 64px) — page title + theme toggle + sign out
 *   · Main content area — generous padding, max-w constrained per page
 *
 * Brand-palette-only (Azure / Cyan / Pulse / Slate ramps + signature
 * gradient). No medical zone colors here — those belong to ZoneBand.
 */

import { Suspense, lazy, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Briefcase,
  ChevronDown,
  FileText,
  FlaskConical,
  Home as HomeIcon,
  LineChart,
  LogOut,
  ShieldCheck,
  Sparkles,
  User,
} from 'lucide-react';
import { logout } from '@cardioq/api-client';
import { useAuth } from '@/hooks/useAuth';
import { signInPathFor } from '@/lib/signInPath';
import { ADMIN_SWITCHER, useInsuranceNavGroup } from '@/lib/insuranceNav';
import { roleLabel } from '@/insurance/lib/persona';
import { Logo } from './Logo';

// Lazy: insurance/lib/api.ts calls getApiClient() at module scope, which
// throws unless configureApiClient() (main.tsx) has already run. DesktopShell
// is part of the app's eager import chain (unlike the lazy-loaded insurance
// routes), so this must stay a dynamic import to not pull that module in early.
const NotificationBell = lazy(() =>
  import('@/insurance/components/NotificationBell').then((m) => ({ default: m.NotificationBell })),
);
import { ThemeToggle } from './ThemeToggle';
import { cn } from '@/lib/cn';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  dev?: boolean;
}

// Dev-only items (the engine sandbox) never ship in a built release.
const NAV: NavItem[] = [
  { to: '/home', label: 'Overview', icon: HomeIcon },
  { to: '/history', label: 'History', icon: LineChart },
  { to: '/documents', label: 'Documents', icon: FileText },
  { to: '/methodology', label: 'How it works', icon: Sparkles },
  // DEV — remove with the /compute route before production
  { to: '/compute', label: 'Compute Test', icon: FlaskConical, dev: true },
  { to: '/profile', label: 'Profile', icon: User },
].filter((item) => !item.dev || import.meta.env.DEV);

function getPageTitle(pathname: string): string {
  if (pathname.startsWith('/home')) return 'Overview';
  if (pathname.startsWith('/score')) return 'CardioQ (Heart) Score';
  if (pathname.startsWith('/ohs')) return 'Wellness Score';
  if (pathname.startsWith('/history')) return 'History';
  if (pathname.startsWith('/documents')) return 'Documents';
  if (pathname.startsWith('/methodology')) return 'How it works';
  if (pathname.startsWith('/compute')) return 'Compute Test';
  if (pathname.startsWith('/profile')) return 'Profile';
  if (pathname.startsWith('/insurance')) return 'Insurance';
  return 'CardioQ';
}

function SidebarLink({ item, active }: { item: NavItem; active: boolean }) {
  const { to, label, icon: Icon, dev } = item;
  return (
    <Link
      to={to}
      className={cn(
        'group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm transition-all focus-ring',
        active
          ? 'bg-brand-azure-500/[0.12] text-brand-slate-50 shadow-[inset_0_0_0_1px_rgba(79,141,241,0.25)]'
          : 'text-brand-slate-400 hover:bg-brand-slate-800/40 hover:text-brand-slate-200',
      )}
    >
      {/* Active accent bar */}
      <span
        aria-hidden
        className={cn(
          'h-5 w-0.5 rounded-full transition-all',
          active ? 'bg-cardioq-gradient' : 'bg-transparent',
        )}
      />
      <Icon
        className={cn(
          'h-4 w-4 transition-colors',
          active ? 'text-brand-cyan-300' : 'text-brand-slate-500 group-hover:text-brand-slate-300',
        )}
      />
      <span className="flex-1">{label}</span>
      {dev ? (
        <span className="rounded-full bg-brand-pulse-400/[0.12] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-brand-pulse-200">
          Dev
        </span>
      ) : null}
    </Link>
  );
}

export function DesktopShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { logout: clearLocalAuth, subjectId, role, user } = useAuth();
  const navigate = useNavigate();

  // Single sidebar, role-aware:
  //   PATIENT   → wellness nav + a collapsible "Insurance" group
  //   EMPLOYER  → CXO-dashboard link + their insurance sections
  //   pro roles → their insurance sections only (wellness is RBAC'd off)
  const insurance = useInsuranceNavGroup();
  const isPatient = role === 'PATIENT';
  const inInsurance = pathname.startsWith('/insurance');
  // Patients can fold the group away; everyone else lives in it.
  const [insOpen, setInsOpen] = useState(!isPatient || inInsurance);
  useEffect(() => {
    if (inInsurance) setInsOpen(true);
  }, [inInsurance]);

  async function handleSignOut() {
    const to = signInPathFor(role);
    await logout();
    clearLocalAuth();
    navigate(to, { replace: true });
  }

  return (
    <div className="relative flex h-screen overflow-hidden bg-brand-bg text-brand-slate-100">
      {/* Ambient brand-gradient glow — barely-there atmospheric tint */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-40"
        style={{
          backgroundImage:
            'radial-gradient(40% 50% at 88% -10%, rgba(34,195,237,0.10), transparent 55%), radial-gradient(40% 60% at 6% 110%, rgba(31,111,229,0.10), transparent 60%)',
        }}
      />

      {/* ===================================================================
          Sidebar — h-screen wrapper pins it; only its internal nav
          scrolls if it overflows. Brand mark + user footer stay sticky.
          =================================================================== */}
      <aside className="relative z-10 flex h-screen w-72 flex-shrink-0 flex-col border-r border-brand-slate-800/60 bg-brand-surface/40 backdrop-blur-xl">
        {/* Brand mark — the official logo image (wordmark is baked in).
            Pros land on their insurance dashboard; patients on /home;
            employers on their CXO dashboard (their main screen). */}
        <Link
          to={
            isPatient ? '/home' : role === 'EMPLOYER' ? '/employer' : (insurance?.base ?? '/home')
          }
          className="flex items-center gap-3 px-6 py-6 focus-ring"
        >
          <Logo className="h-11 w-11" />
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-brand-slate-500">
            {isPatient ? 'Patient' : 'Insurance'}
          </p>
        </Link>

        {/* Nav — flex-1 fills the gap between mark and footer; if it
            ever exceeds the available space, it gets its own scroll so
            the mark + footer stay pinned. */}
        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-2">
          {isPatient
            ? NAV.map((item) => (
                <SidebarLink key={item.to} item={item} active={pathname.startsWith(item.to)} />
              ))
            : null}

          {/* Employer pros keep one foot in the wellness CXO dashboard. */}
          {role === 'EMPLOYER' ? (
            <SidebarLink
              item={{ to: '/employer', label: 'CXO dashboard', icon: Briefcase }}
              active={pathname.startsWith('/employer')}
            />
          ) : null}

          {/* =============================================================
              Insurance group — the section's entire nav lives here on
              desktop (the dashboards' own tabs only render < md).
              ============================================================= */}
          {insurance ? (
            <div className={cn(isPatient && 'mt-2 border-t border-brand-slate-800/60 pt-2')}>
              <button
                type="button"
                onClick={() => (isPatient ? setInsOpen((o) => !o) : navigate(insurance.base))}
                aria-expanded={insOpen}
                className={cn(
                  'group flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm transition-all focus-ring',
                  inInsurance
                    ? 'text-brand-slate-50'
                    : 'text-brand-slate-400 hover:bg-brand-slate-800/40 hover:text-brand-slate-200',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'h-5 w-0.5 rounded-full transition-all',
                    inInsurance ? 'bg-cardioq-gradient' : 'bg-transparent',
                  )}
                />
                <ShieldCheck
                  className={cn(
                    'h-4 w-4 transition-colors',
                    inInsurance
                      ? 'text-brand-cyan-300'
                      : 'text-brand-slate-500 group-hover:text-brand-slate-300',
                  )}
                />
                <span className="flex-1 text-left">Insurance</span>
                {isPatient ? (
                  <ChevronDown
                    className={cn(
                      'h-3.5 w-3.5 text-brand-slate-500 transition-transform',
                      insOpen && 'rotate-180',
                    )}
                  />
                ) : null}
              </button>

              {/* Super-user god mode — jump between persona dashboards. */}
              {insOpen && insurance.own === 'admin' ? (
                <label className="relative mb-1 ml-9 mr-2 flex items-center">
                  <select
                    value={insurance.persona}
                    onChange={(e) => navigate(`/insurance/${e.target.value}`)}
                    aria-label="Switch dashboard (Super Admin god mode)"
                    className="w-full cursor-pointer appearance-none rounded-lg border border-brand-slate-700/60 bg-brand-slate-900/40 py-1 pl-2.5 pr-7 text-xs font-medium text-brand-slate-300 hover:border-brand-slate-600 focus-ring"
                  >
                    {ADMIN_SWITCHER.map((s) => (
                      <option key={s.persona} value={s.persona}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2 h-3.5 w-3.5 text-brand-slate-500" />
                </label>
              ) : null}

              {insOpen
                ? insurance.items.map((item, idx) => {
                    // The first section is each persona's default, so the
                    // bare /insurance/<persona> path highlights it too.
                    const active =
                      pathname === item.to || (idx === 0 && pathname === insurance.base);
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={cn(
                          'group flex items-center gap-3 rounded-xl py-2 pl-9 pr-3.5 text-[13px] transition-all focus-ring',
                          active
                            ? 'bg-brand-azure-500/[0.12] text-brand-slate-50 shadow-[inset_0_0_0_1px_rgba(79,141,241,0.25)]'
                            : 'text-brand-slate-400 hover:bg-brand-slate-800/40 hover:text-brand-slate-200',
                        )}
                      >
                        <Icon
                          className={cn(
                            'h-4 w-4 transition-colors',
                            active
                              ? 'text-brand-cyan-300'
                              : 'text-brand-slate-500 group-hover:text-brand-slate-300',
                          )}
                        />
                        <span className="flex-1">{item.label}</span>
                      </Link>
                    );
                  })
                : null}
            </div>
          ) : null}
        </nav>

        {/* User footer */}
        <div className="border-t border-brand-slate-800/60 px-3 py-3">
          <div className="flex items-center gap-3 rounded-xl px-3 py-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-azure-500/20 ring-1 ring-brand-azure-400/30">
              <User className="h-4 w-4 text-brand-azure-300" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs text-brand-slate-200">
                {user?.name ?? 'Signed in'}
              </p>
              <p className="truncate font-mono text-[10px] uppercase tracking-wide text-brand-slate-500">
                {isPatient ? (subjectId?.slice(0, 8) ?? 'patient') : roleLabel(role)}
              </p>
            </div>
            <ThemeToggle />
          </div>
        </div>
      </aside>

      {/* ===================================================================
          Main column — top bar + content
          =================================================================== */}
      <div className="relative z-10 flex flex-1 flex-col">
        {/* Sticky top bar */}
        <header className="sticky top-0 z-20 border-b border-brand-slate-800/60 bg-brand-bg/70 backdrop-blur-xl">
          <div className="flex h-16 items-center gap-4 px-8">
            <h1 className="font-display text-base tracking-tight text-brand-slate-100">
              {getPageTitle(pathname)}
            </h1>
            <div className="flex-1" />
            {['INSURER', 'TPA', 'HOSPITAL_BILLING', 'PATIENT', 'EMPLOYER'].includes(role ?? '') ? (
              <Suspense fallback={null}>
                <NotificationBell
                  persona={role as 'INSURER' | 'TPA' | 'HOSPITAL_BILLING' | 'PATIENT' | 'EMPLOYER'}
                />
              </Suspense>
            ) : null}
            <button
              type="button"
              onClick={handleSignOut}
              className="inline-flex items-center gap-2 rounded-full border border-brand-slate-700/60 bg-brand-slate-900/40 px-4 py-1.5 text-sm text-brand-slate-300 transition-colors hover:border-brand-slate-600 hover:bg-brand-slate-800/40 hover:text-brand-slate-100 focus-ring"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-6xl px-8 py-10">{children}</div>
        </main>
      </div>
    </div>
  );
}
